<template>
  <div v-if="show_button">
    <button class="btn btn-sm btn-warning" @click="open_dialog">{{ button_label }}</button>

    <dialog ref="dialog" id="elevate_modal" class="modal">
      <div class="modal-box w-112 max-w-none">
        <h3 class="mb-4 text-lg font-bold">Nâng quyền</h3>

        <!-- module source radio -->
        <div class="form-control mb-4">
          <label class="label cursor-pointer">
            <span class="label-text">Chọn file từ máy</span>
            <input
              type="radio"
              v-model="source"
              value="machine"
              class="radio radio-primary"
            />
          </label>
          <label class="label cursor-pointer">
            <span class="label-text">Chọn file đã lưu</span>
            <input
              type="radio"
              v-model="source"
              value="saved"
              class="radio radio-primary"
            />
          </label>
        </div>

        <!-- source: upload from machine -->
        <section v-if="source === 'machine'" class="mb-4">
          <input
            type="file"
            class="input input-bordered w-full"
            @change="on_file_selected"
          />
          <p v-if="selected_file" class="mt-2 text-sm text-base-content/70">
            {{ selected_file.name }} — {{ format_size(selected_file.size) }}
          </p>
          <label class="mt-3 block text-left text-sm">Mô tả (tùy chọn)</label>
          <textarea
            v-model="description"
            class="textarea textarea-bordered w-full"
            placeholder="Ghi chú về module nâng quyền..."
          ></textarea>
        </section>

        <!-- source: choose saved file -->
        <section v-if="source === 'saved'" class="mb-4">
          <input
            type="search"
            class="input input-bordered w-full mb-2"
            placeholder="Lọc file đã lưu..."
            @input="on_keyword_input"
          />
          <div class="max-h-48 overflow-auto">
            <table class="table table-sm">
              <tbody>
                <tr v-if="saved_versions.length === 0">
                  <td class="text-base-content/50">Không có file đã lưu.</td>
                </tr>
                <tr
                  v-for="v in saved_versions"
                  :key="String(v.id)"
                  :class="{ 'bg-primary/10': selected_version_id === String(v.id) }"
                  class="cursor-pointer"
                  @click="select_saved_version(v)"
                >
                  <td class="min-w-64">
                    <div class="font-medium truncate">{{ v.filename ?? "—" }}</div>
                    <div v-if="v.description" class="text-xs text-base-content/50 truncate">{{ v.description }}</div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <!-- ponytail: read-only description of the file just clicked; user previews before elevating. -->
          <p v-if="previewed_version" class="mt-3 text-sm rounded bg-base-200/60 p-2">
            <span class="font-medium">{{ previewed_version.filename ?? "—" }}</span>
            <span v-if="previewed_version.description" class="block text-base-content/70 mt-1">
              {{ previewed_version.description }}
            </span>
          </p>
        </section>

        <div class="modal-action">
          <form method="dialog" class="flex gap-2">
            <button class="btn" value="cancel">Hủy</button>
            <button
              class="btn btn-primary min-w-28"
              :disabled="!can_submit || action_store.loading.create_action"
              @click="handle_confirm"
            >
              <span v-if="action_store.loading.create_action"><Loading /></span>
              <span v-else>Nâng quyền</span>
            </button>
          </form>
        </div>
      </div>
    </dialog>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useRoute } from "vue-router";
import Loading from "@/components/LoadingTemplate.vue";
import { toast } from "vue3-toastify";
import { useQueryClient } from "@tanstack/vue-query";
import { AxiosError as AxiosErrorClass } from "axios";
import { use_action } from "./ExplorerV2/useAction";
import { use_client_detail } from "./useClientDetail";
import { createElevateAction, elevateModuleType, file_to_module_b64, listElevateVersions } from "@/api/action";
import type { SavedVersion } from "@/api/action";

const action_store = use_action();
const query_client = useQueryClient();
const client_detail_store = use_client_detail();
const route = useRoute();

const ELEVATE_ADMIN = "elevate_admin" as const;
const ELEVATE_SYSTEM = "elevate_system" as const;
const STANDARD = "standard user";
const ADMINISTRATOR = "administrator";

// Which elevation this client is eligible for, or null when neither applies.
// Derived reactively from the current privilege so it updates automatically
// whenever the active client (route) changes — no manual refresh needed.
type Elevatable = typeof ELEVATE_ADMIN | typeof ELEVATE_SYSTEM;
const elevatable_action = computed<Elevatable | null>(() => {
  // Backend may return the permission with mixed case ("Standard User",
  // "Administrator"); normalize to lowercase before comparing to constants.
  const current = String(client_detail_store.client_detail?.privileges?.permission ?? "")
    .toLowerCase();
  if (current === STANDARD) return ELEVATE_ADMIN;
  if (current === ADMINISTRATOR) return ELEVATE_SYSTEM;
  return null;
});

const show_button = computed(() => elevatable_action.value !== null);
const button_label = computed<string>(() => {
  switch (elevatable_action.value) {
    case ELEVATE_ADMIN:
      return "Nâng quyền Administrator";
    case ELEVATE_SYSTEM:
      return "Nâng quyền SYSTEM";
    default:
      return "";
  }
});

const dialog = ref<HTMLDialogElement | null>(null);
const open_dialog = () => {
  if (dialog.value) dialog.value.showModal();
  load_saved_versions();
};

// form state
const source = ref<"machine" | "saved">("machine");
const selected_file = ref<File | null>(null);
const description = ref("");
const saved_versions = ref<SavedVersion[]>([]);
const selected_version_id = ref("");
// ponytail: preview var holds the clicked file's full record for read-only display;
// clicking previews/highlights but does not elevate — elevation needs an explicit submit.
const previewed_version = ref<SavedVersion | null>(null);

const can_submit = computed(() =>
  source.value === "machine" ? !!selected_file.value : !!selected_version_id.value,
);

const format_size = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const on_file_selected = (e: Event) => {
  const target = e.target as HTMLInputElement;
  selected_file.value = target.files?.[0] ?? null;
};

// ponytail: saved-file picker scopes to exactly one privilege-target storage type.
// Derived from elevatable_action (already privilege-driven) — never the generic "elevate" bucket.
const moduleType = computed<"elevate-admin" | "elevate-system">(() =>
  elevateModuleType[elevatable_action.value!],
);

let keyword_timer: ReturnType<typeof setTimeout> | null = null;
const load_saved_versions = async (keyword?: string) => {
  saved_versions.value = await listElevateVersions({ type: moduleType.value, keyword });
};
const on_keyword_input = (e: Event) => {
  const kw = (e.target as HTMLInputElement).value.trim();
  if (keyword_timer) clearTimeout(keyword_timer);
  keyword_timer = setTimeout(() => load_saved_versions(kw || undefined), 300);
};

const select_saved_version = (v: Record<string, unknown>) => {
  selected_version_id.value = String(v.id);
  previewed_version.value = v as unknown as SavedVersion;
};

const handle_confirm = async () => {
  try {
    const agentId = route.params.client_id as string;
    if (!agentId) {
      toast.error("Thiếu ID workstation.");
      return;
    }

    let module_b64: string | undefined;
    let version: string | undefined;

    if (source.value === "machine") {
      if (!selected_file.value) {
        toast.error("Vui lòng chọn file để nâng quyền.");
        return;
      }
      module_b64 = await file_to_module_b64(selected_file.value);
    } else {
      version = selected_version_id.value;
    }

    if (!elevatable_action.value) {
      toast.error("Thiếu loại nâng quyền.");
      return;
    }

    // ponytail: saved-module description comes from the previewed record (no editable textarea for saved source);
    // machine-source keeps its manual description box.
    const active_description = source.value === "machine" ? description.value : previewed_version.value?.description;
    const action_id = await createElevateAction({
      agentId,
      action: elevatable_action.value,
      description: active_description || undefined,
      version,
      module_b64,
      original_file_name:
        source.value === "machine" ? selected_file.value?.name : undefined,
    });

    if (dialog.value) dialog.value.close();

    query_client.invalidateQueries({ queryKey: ["action_history"] });

    action_store.watch_for_action(action_id, {
      on_complete: () => toast.success("Nâng quyền thành công."),
      on_fail: () => toast.error("Nâng quyền thất bại."),
    });
  } catch (err) {
    if (err instanceof AxiosErrorClass && err.response?.data?.message) {
      toast.error(err.response.data.message);
    } else {
      toast.error("Đã xảy ra lỗi khi nâng quyền.");
    }
  }
};
</script>
